<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as JobEditCancelJobDialogFormInitialData,
  ValidData as JobEditCancelJobDialogFormValidData,
} from '@/components/dialogForms/JobEditCancelJobDialogForm.vue';

import { computed, useTemplateRef } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import JobEditCancelJobDialogForm from '@/components/dialogForms/JobEditCancelJobDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  isConfirmLoading?: boolean;
  jobId?: number;
  jobDate?: string;
  actor?: string;
  reason?: string;
  confirm?: (formData: JobEditCancelJobDialogFormValidData) => Promise<unknown>;
}
export type DefProps = typeof props;

const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  jobId: undefined,
  jobDate: undefined,
  actor: undefined,
  reason: undefined,
  confirm: undefined,
});

const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: JobEditCancelJobDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: JobEditCancelJobDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef<HTMLElement>('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<JobEditCancelJobDialogFormInitialData>(() => {
  return {
    actor: props.actor,
    reason: props.reason,
  };
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.jobEditCancelJobDialog"
    data-testid="jobEditCancelJobDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        Cancel job
      </ReportGenerateDialogHeader>
    </template>

    <section ref="main">
      <div :class="$style.text">
        Provide the reason and actor for canceling this job.
      </div>
      <JobEditCancelJobDialogForm
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
    </section>

    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :disabled="props.isConfirmLoading"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-check"
            :loading="props.isConfirmLoading"
            @click="handleConfirmClick"
          >
            Confirm
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.jobEditCancelJobDialog {
  width: variables.$reportGenerateDialogWidth;
}
.text {
  margin: 0 0 math.div(variables.$pageDialogWindowMainWrapperMargin, 2) 0;
}
.dialogFooterMenuList {
  list-style: none;
  margin: 0;
  padding: 0;
  text-align: right;
}
.dialogFooterMenuListItem {
  display: inline;
}
.dialogFooterMenuListItem + .dialogFooterMenuListItem {
  margin-left: 1rem;
}
</style>
